import { useMemo, useState } from 'react';
import { Thumb } from '../components/ImagePick';
import { Link } from 'react-router-dom';
import { articles as Api, type Article } from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';
import { Shim, SkeletonRows } from '../components/Shimmer';
import { useSelection, TickAll, Tick, BulkActions } from '../components/Bulk';
import { stampDay } from '../lib/time';

const day = stampDay;

const FILTERS = ['all', 'published', 'draft'] as const;

export function Library() {
  const { data, error, loading, reload } = useAsync((signal) => Api.list(signal));
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>('all');

  const rows = useMemo(() => {
    const all = data?.articles ?? [];
    return filter === 'all' ? all : all.filter((a) => a.status === filter);
  }, [data, filter]);

  const counts = useMemo(() => {
    const all = data?.articles ?? [];
    return {
      published: all.filter((a) => a.status === 'published').length,
      draft: all.filter((a) => a.status === 'draft').length,
    };
  }, [data]);

  async function remove(a: Article) {
    if (!confirm(`Delete “${a.title}”? This cannot be undone.`)) return;
    await Api.remove(a.id);
    await reload();
  }

  const sel = useSelection(rows);

  return (
    <div className="pg">
      <div className="rowbar">
        <div>
          <h1 style={{ fontSize: 29 }}>Content library</h1>
          <p style={{ fontSize: 13.5, color: 'var(--tx2)', marginTop: 7 }}>
            {loading && !data ? <Shim w={210} h={12} style={{ marginTop: 3 }} />
              : `${counts.published} published · ${counts.draft} draft${counts.draft === 1 ? '' : 's'}`}
          </p>
        </div>
        {/* The long-form editor is still the old console's: a rich body, a
            cover upload and scheduling do not belong in a dialog, and it is a
            page of its own rather than a list. */}
        <Link className="b bg" to="/articles/new" style={{ textDecoration: 'none' }}>New article</Link>
      </div>

      {error ? <div className="err">Could not load the library — {error.message}</div> : null}

      <div className="crd" style={{ padding: '20px 22px' }}>
        <div className="sg" style={{ marginBottom: 16 }}>
          {FILTERS.map((f) => (
            <span key={f} {...(filter === f ? { 'data-on': '' } : {})} onClick={() => setFilter(f)}>
              {f === 'all' ? 'All' : f === 'published' ? 'Published' : 'Drafts'}
            </span>
          ))}
        </div>

        <BulkActions
          path="/articles" noun="article"
          rows={sel.picked.map((a) => ({ id: a.id, label: String(a.title || 'Untitled') }))}
          actions={[['publish', 'Publish'], ['unpublish', 'Unpublish']]}
          onDone={async () => { sel.clear(); await reload(); }}
        />

        <div className="tblx">
          <table className="tbl">
            <thead>
              <tr>
                <TickAll on={sel.allOn} some={sel.picked.length > 0} onChange={sel.toggleAll} /><th>Headline</th><th>Category</th><th>Access</th><th>Status</th><th>Updated</th><th /></tr>
            </thead>
            <tbody>
              {loading && !data ? (
                <SkeletonRows cols={7} tick />
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="muted" style={{ padding: '26px 8px' }}>
                  {data?.articles.length ? 'Nothing with that status.' : 'Nothing written yet.'}
                </td></tr>
              ) : rows.map((a) => (
                <tr key={a.id} style={sel.isOn(a.id) ? { background: 'rgba(var(--goldrgb), .05)' } : undefined}>
                  <Tick on={sel.isOn(a.id)} label={String(a.title || 'Untitled')}
                    onChange={(v) => sel.toggle(a.id, v)} />
                  <td>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <Thumb src={a.cover_path} width={60} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700 }}>{a.title || 'Untitled'}</div>
                        {a.standfirst ? (
                          <div style={{ fontSize: 11.5, color: 'var(--tx3)', maxWidth: 460, overflow: 'hidden',
                            textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.standfirst}</div>
                        ) : null}
                      </div>
                    </div>
                  </td>
                  <td className="muted">{a.category}</td>
                  <td className="muted">{a.access}</td>
                  <td>
                    <span className="tg" style={{
                      background: a.status === 'published' ? 'rgba(62,207,142,.14)' : 'rgba(var(--wash), calc(.06 * var(--washx)))',
                    }}>{a.status}</span>
                  </td>
                  <td className="muted">{day(a.updated_at)}</td>
                  <td className="right" style={{ whiteSpace: 'nowrap' }}>
                    <Link className="b bo" style={{ padding: '6px 12px', fontSize: 11.5, textDecoration: 'none' }}
                      to={`/articles/${a.id}`}>Edit</Link>
                    <button className="b bo" style={{ padding: '6px 12px', fontSize: 11.5, marginLeft: 6 }}
                      type="button" onClick={() => void remove(a)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
